// Query UI — shell-only at the alpha milestone.
//
// Per v2.1 §10.4, Frame-QL execution lands in Phase 5 once
// `coframe.ql` (parser) + `coframe.resolution` (planner/executor) ship.
// Slice 8 puts the surfaces in place so the three-UI navigation is
// fully populated and the operator can already SEE what the active AC
// exposes (dimensions, metrics, schemas) — composing a query mentally
// is useful even before Run is wired.
//
// Layout when a session is active:
//   ┌─── ACBrowser ──────────┬──── Frame-QL panel ─────┐
//   │  ac_name + description │  textarea + Run (disabled)│
//   │  Dimensions (per fam)  │  Phase-5 note            │
//   │  Metrics  (per fam)    ├──── NL Query panel ──────┤
//   │  Schemas  (per name)   │  input + Ask (disabled)  │
//   │                        │  Phase-5 note            │
//   │                        ├──── Results panel ───────┤
//   │                        │  empty placeholder       │
//   └────────────────────────┴──────────────────────────┘
//
// When no session: a hint to open one in Workbench first.

import { useCallback, useEffect, useState } from "react";
import {
  ApiError,
  type DraftResponse,
  type QueryFrameResponse,
  type StructuredQueryError,
} from "../api/client";
import { useSession } from "../workbench/SessionContext";

export function QueryPage() {
  const { client, session } = useSession();
  const [draft, setDraft] = useState<DraftResponse["draft_ac"]>(null);
  const [error, setError] = useState<string | null>(null);
  const [framqlText, setFramqlText] = useState("");
  const [nlqText, setNlqText] = useState("");
  const [result, setResult] = useState<QueryFrameResponse | null>(null);
  const [queryError, setQueryError] = useState<QueryErrorState | null>(null);
  const [running, setRunning] = useState(false);

  const loadDraft = useCallback(async () => {
    if (session === null) return;
    try {
      const r = await client.getDraft(session.session_id);
      setDraft(r.draft_ac);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [client, session]);

  useEffect(() => {
    setDraft(null);
    setError(null);
    setResult(null);
    setQueryError(null);
    if (session !== null) loadDraft();
  }, [session, loadDraft]);

  const runQuery = useCallback(async () => {
    if (session === null || framqlText.trim() === "") return;
    setRunning(true);
    setQueryError(null);
    setResult(null);
    try {
      const r = await client.runQuery(session.session_id, {
        source: framqlText,
      });
      setResult(r);
    } catch (e) {
      setQueryError(parseQueryError(e));
    } finally {
      setRunning(false);
    }
  }, [client, session, framqlText]);

  if (session === null) {
    return (
      <section>
        <h1>Query</h1>
        <p style={subtitleStyle}>
          Frame-QL / NL Query against the active AC. Shell-only at alpha —
          the executor lands when <code>coframe.ql</code> +{" "}
          <code>coframe.resolution</code> ship in Phase 5.
        </p>
        <div style={emptyStateStyle}>
          <p style={{ margin: 0, color: "#666" }}>
            Open a session in the <strong>Workbench</strong> first — the
            Query UI reads the active AC to populate the dimension / metric /
            schema browser.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section>
      <h1>Query</h1>
      <p style={subtitleStyle}>
        Compose Frame-QL or natural-language questions against the active
        AC. Shell-only — Run / Ask are disabled until the Phase-5 resolver
        lands. The left pane already reflects what the AC exposes today.
      </p>

      {error && (
        <div style={errorBlockStyle}>
          <strong>Error:</strong> {error}
        </div>
      )}

      <div style={bodyStyle}>
        <ACBrowser draft={draft} />
        <div style={mainColStyle}>
          <FrameQLPanel
            value={framqlText}
            onChange={setFramqlText}
            knownMetrics={metricNames(draft)}
            knownDims={dimNames(draft)}
            onRun={runQuery}
            running={running}
          />
          <NLQPanel value={nlqText} onChange={setNlqText} />
          <ResultsPanel result={result} error={queryError} running={running} />
        </div>
      </div>
    </section>
  );
}

// ── AC Browser (read-only) ────────────────────────────────────────────

interface ACBrowserProps {
  draft: DraftResponse["draft_ac"];
}

function ACBrowser({ draft }: ACBrowserProps) {
  if (draft === null) {
    return (
      <aside style={browserStyle}>
        <div style={browserHeaderStyle}>Lattice</div>
        <p style={{ color: "#888", fontSize: "0.85rem" }}>
          No draft Lattice in this session yet. Use <em>Declare</em> in
          Build to initialize one.
        </p>
      </aside>
    );
  }

  const dims = draft.dimension_families ?? [];
  const metrics = draft.metric_families ?? [];
  const schemas = draft.schemas ?? [];

  return (
    <aside style={browserStyle}>
      <div style={browserHeaderStyle}>
        AC: <code>{draft.ac_name}</code>
      </div>
      {draft.ac_description && (
        <p style={{ margin: "0 0 0.75rem", color: "#666", fontSize: "0.8rem" }}>
          {String(draft.ac_description)}
        </p>
      )}

      <BrowserSection label="Dimensions" count={dims.length}>
        {dims.length === 0 ? (
          <Empty />
        ) : (
          dims.map((d) => (
            <details key={d.name} style={detailsStyle}>
              <summary style={summaryStyle}>
                <code>{d.name}</code>
                <span style={countBadgeStyle}>{d.members.length}</span>
              </summary>
              <ul style={memberListStyle}>
                {d.members.map((m) => (
                  <li key={m}>
                    <code>{m}</code>
                  </li>
                ))}
              </ul>
            </details>
          ))
        )}
      </BrowserSection>

      <BrowserSection label="Metrics" count={metrics.length}>
        {metrics.length === 0 ? (
          <Empty />
        ) : (
          metrics.map((m) => (
            <div key={m.name} style={metricRowStyle}>
              <code>{m.name}</code>
            </div>
          ))
        )}
      </BrowserSection>

      <BrowserSection label="Schemas" count={schemas.length}>
        {schemas.length === 0 ? (
          <Empty />
        ) : (
          schemas.map((s) => (
            <div key={s.schema_name} style={metricRowStyle}>
              <code>{s.schema_name}</code>
              <span style={countBadgeStyle}>{s.column_specs.length} col</span>
            </div>
          ))
        )}
      </BrowserSection>
    </aside>
  );
}

function BrowserSection(props: {
  label: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <div style={browserSectionStyle}>
      <div style={browserSectionHeaderStyle}>
        <span>{props.label}</span>
        <span style={sectionCountStyle}>{props.count}</span>
      </div>
      {props.children}
    </div>
  );
}

function Empty() {
  return <p style={emptyHintStyle}>none yet</p>;
}

// ── Frame-QL panel ────────────────────────────────────────────────────

interface FrameQLPanelProps {
  value: string;
  onChange: (s: string) => void;
  knownMetrics: string[];
  knownDims: string[];
  onRun: () => void;
  running: boolean;
}

function FrameQLPanel({
  value,
  onChange,
  knownMetrics,
  knownDims,
  onRun,
  running,
}: FrameQLPanelProps) {
  // Pre-fill a hint with the first metric / dim if the textarea is empty
  // — composing your first query is much faster when the placeholder is
  // grounded in YOUR AC's vocabulary rather than a generic toy example.
  const placeholder = composePlaceholder(knownMetrics, knownDims);
  const disabled = running || value.trim() === "";

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    // Ctrl/⌘+Enter runs the query — the conventional notebook shortcut.
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      if (!disabled) onRun();
    }
  }

  return (
    <div style={panelStyle}>
      <div style={panelHeaderStyle}>
        <span>Frame-QL</span>
      </div>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        rows={6}
        style={textareaStyle}
        spellCheck={false}
      />
      <div style={panelFooterStyle}>
        <button
          type="button"
          onClick={onRun}
          disabled={disabled}
          style={disabled ? runButtonDisabledStyle : runButtonStyle}
        >
          {running ? "Running…" : "Run"}
        </button>
        <span style={hintStyle}>
          Press ⌘/Ctrl + Enter to run.
        </span>
      </div>
    </div>
  );
}

function composePlaceholder(metrics: string[], dims: string[]): string {
  const metric = metrics[0];
  const dim = dims[0];
  const dim2 = dims[1];
  if (metric && dim && dim2) {
    // Two-dim case: showcase LIMIT n PER (§8.5.7) — the flagship
    // top-N-per-group form that SQL otherwise needs a window function for.
    return (
      `// e.g.\n` +
      `SELECT ${dim}, ${dim2}, ${metric}\n` +
      `AT (${dim}, ${dim2})\n` +
      `ORDER BY ${metric} DESC\n` +
      `LIMIT 3 PER ${dim}`
    );
  }
  if (metric && dim) {
    return `// e.g.\n${metric} by ${dim}`;
  }
  return "// Declare a metric family + dimension family in the Workbench first.";
}

// ── NLQ panel ─────────────────────────────────────────────────────────

interface NLQPanelProps {
  value: string;
  onChange: (s: string) => void;
}

function NLQPanel({ value, onChange }: NLQPanelProps) {
  return (
    <div style={panelStyle}>
      <div style={panelHeaderStyle}>
        <span>Natural-language query</span>
        <span style={phaseHintStyle}>NLQ dialogue in Phase 5+</span>
      </div>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Ask a question in plain English…"
        style={nlqInputStyle}
      />
      <div style={panelFooterStyle}>
        <button
          type="button"
          disabled
          title="NLQ dialogue lands in Phase 5+"
          style={runButtonDisabledStyle}
        >
          Ask
        </button>
        <span style={hintStyle}>
          The NLQ pipeline (intent → Frame-QL → clarify) lands with the
          resolver.
        </span>
      </div>
    </div>
  );
}

// ── Results panel ─────────────────────────────────────────────────────

interface QueryErrorState {
  code: string;
  message: string;
  context: Record<string, unknown>;
}

interface ResultsPanelProps {
  result: QueryFrameResponse | null;
  error: QueryErrorState | null;
  running: boolean;
}

function ResultsPanel({ result, error, running }: ResultsPanelProps) {
  return (
    <div style={panelStyle}>
      <div style={panelHeaderStyle}>
        <span>Results</span>
        {result !== null && (
          <span style={resultsHeaderRightStyle}>
            <ServedFromBadge servedFrom={result.served_from ?? null} />
            <span style={countBadgeStyle}>
              {result.row_count} row{result.row_count === 1 ? "" : "s"}
            </span>
          </span>
        )}
      </div>
      {error !== null ? (
        <ResultsError err={error} />
      ) : result !== null ? (
        <ResultsTable result={result} />
      ) : (
        <div style={resultsPlaceholderStyle}>
          <p style={{ margin: 0 }}>
            {running
              ? "Running query against the backend…"
              : "Type a Frame-QL query above and press Run."}
          </p>
        </div>
      )}
    </div>
  );
}

/** D4: small badge showing which path served the Frame.
 * Renders nothing when served_from is null (pre-D4 server). */
function ServedFromBadge({
  servedFrom,
}: {
  servedFrom: QueryFrameResponse["served_from"];
}) {
  if (!servedFrom) return null;
  const label = SERVED_FROM_LABELS[servedFrom];
  const style = SERVED_FROM_STYLES[servedFrom];
  return (
    <span style={style} title={SERVED_FROM_DESCRIPTIONS[servedFrom]}>
      {label}
    </span>
  );
}

const SERVED_FROM_LABELS: Record<
  NonNullable<QueryFrameResponse["served_from"]>,
  string
> = {
  engine_cache: "Engine cache",
  engine_mixed: "Engine + backend",
  engine_backend: "Engine → backend",
  backend: "Backend",
};

const SERVED_FROM_DESCRIPTIONS: Record<
  NonNullable<QueryFrameResponse["served_from"]>,
  string
> = {
  engine_cache:
    "All metrics served from the Metric Engine's cache or FD-DAG rollup. Backend was NOT invoked.",
  engine_mixed:
    "Some metrics served from cache, others required a backend call + memoisation.",
  engine_backend:
    "Cold cache — every metric went to the backend. Results memoised so next call is a cache hit.",
  backend:
    "Metric Engine disabled for this installation; legacy direct-backend path.",
};

const SERVED_FROM_STYLES: Record<
  NonNullable<QueryFrameResponse["served_from"]>,
  React.CSSProperties
> = {
  engine_cache: {
    fontSize: "0.7rem",
    color: "#0a6e1c",
    background: "#e1f5e6",
    border: "1px solid #b3e0bd",
    borderRadius: 3,
    padding: "0.05rem 0.4rem",
    fontWeight: 600,
  },
  engine_mixed: {
    fontSize: "0.7rem",
    color: "#7a5300",
    background: "#fff5d6",
    border: "1px solid #e6d8a8",
    borderRadius: 3,
    padding: "0.05rem 0.4rem",
    fontWeight: 600,
  },
  engine_backend: {
    fontSize: "0.7rem",
    color: "#5a4500",
    background: "#faf3e0",
    border: "1px solid #e6d8a8",
    borderRadius: 3,
    padding: "0.05rem 0.4rem",
    fontWeight: 600,
  },
  backend: {
    fontSize: "0.7rem",
    color: "#555",
    background: "#eee",
    border: "1px solid #ccc",
    borderRadius: 3,
    padding: "0.05rem 0.4rem",
    fontWeight: 600,
  },
};

const resultsHeaderRightStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "0.4rem",
};

function ResultsError({ err }: { err: QueryErrorState }) {
  return (
    <div style={errorCardStyle}>
      <div style={errorCodeStyle}>{err.code}</div>
      <div style={errorMessageStyle}>{err.message}</div>
      {Object.keys(err.context).length > 0 && (
        <details style={errorContextStyle}>
          <summary style={{ cursor: "pointer" }}>Diagnostic context</summary>
          <pre style={errorContextPreStyle}>
            {JSON.stringify(err.context, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}

function ResultsTable({ result }: { result: QueryFrameResponse }) {
  if (result.row_count === 0) {
    return (
      <div style={resultsPlaceholderStyle}>
        <p style={{ margin: 0 }}>Query returned 0 rows.</p>
      </div>
    );
  }
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={resultsTableStyle}>
        <thead>
          <tr>
            {result.columns.map((c) => (
              <th key={c} style={resultsHeadStyle}>
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {result.rows.map((row, i) => (
            <tr key={i} style={i % 2 ? resultsRowAltStyle : undefined}>
              {row.map((cell, j) => (
                <td key={j} style={resultsCellStyle}>
                  {formatCell(cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function formatCell(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "number") {
    // Light formatting: thousands separator for ints, fixed for floats.
    if (Number.isInteger(v)) return v.toLocaleString();
    return v.toLocaleString(undefined, { maximumFractionDigits: 4 });
  }
  if (typeof v === "string") return v;
  return JSON.stringify(v);
}

/** Turn an ApiError into a QueryErrorState. The /query endpoint returns
 * `{detail: {error: {code, message, context}}}` for structured refusals;
 * generic errors get an "unknown" code. */
function parseQueryError(e: unknown): QueryErrorState {
  if (e instanceof ApiError) {
    const body = e.body as { detail?: StructuredQueryError } | undefined;
    const structured = body?.detail?.error;
    if (structured && typeof structured.code === "string") {
      return {
        code: structured.code,
        message: structured.message,
        context: structured.context ?? {},
      };
    }
    return { code: "http_error", message: e.message, context: { status: e.status } };
  }
  return {
    code: "unknown",
    message: e instanceof Error ? e.message : String(e),
    context: {},
  };
}

// ── Helpers ───────────────────────────────────────────────────────────

function dimNames(draft: DraftResponse["draft_ac"]): string[] {
  return (draft?.dimension_families ?? []).map((d) => d.name);
}

function metricNames(draft: DraftResponse["draft_ac"]): string[] {
  return (draft?.metric_families ?? []).map((m) => m.name);
}

// ── Styles ────────────────────────────────────────────────────────────

const subtitleStyle: React.CSSProperties = {
  color: "#666",
  fontSize: "0.9rem",
  marginTop: "-0.5rem",
};
const bodyStyle: React.CSSProperties = {
  display: "flex",
  gap: "1.25rem",
  alignItems: "stretch",
  marginTop: "1rem",
};
const browserStyle: React.CSSProperties = {
  width: 260,
  flexShrink: 0,
  padding: "0.75rem",
  borderRight: "1px solid #eee",
  background: "#fafafa",
  fontSize: "0.85rem",
};
const browserHeaderStyle: React.CSSProperties = {
  fontWeight: 600,
  fontSize: "0.85rem",
  textTransform: "uppercase",
  letterSpacing: "0.02em",
  color: "#444",
  marginBottom: "0.5rem",
};
const browserSectionStyle: React.CSSProperties = {
  marginTop: "0.75rem",
};
const browserSectionHeaderStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
  fontWeight: 600,
  fontSize: "0.75rem",
  textTransform: "uppercase",
  letterSpacing: "0.02em",
  color: "#666",
  marginBottom: "0.25rem",
  borderBottom: "1px solid #eee",
  paddingBottom: "0.2rem",
};
const sectionCountStyle: React.CSSProperties = {
  fontWeight: 400,
  color: "#aaa",
  fontSize: "0.7rem",
};
const detailsStyle: React.CSSProperties = {
  marginBottom: "0.2rem",
};
const summaryStyle: React.CSSProperties = {
  cursor: "pointer",
  padding: "0.15rem 0",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "0.5rem",
};
const memberListStyle: React.CSSProperties = {
  listStyle: "none",
  margin: "0.15rem 0 0.4rem 0.75rem",
  padding: 0,
  fontSize: "0.8rem",
  color: "#444",
};
const metricRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "0.2rem 0",
};
const countBadgeStyle: React.CSSProperties = {
  fontSize: "0.7rem",
  color: "#888",
  background: "#eee",
  borderRadius: 3,
  padding: "0.05rem 0.35rem",
};
const emptyHintStyle: React.CSSProperties = {
  margin: "0.15rem 0",
  color: "#aaa",
  fontStyle: "italic",
  fontSize: "0.8rem",
};

const mainColStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
};
const panelStyle: React.CSSProperties = {
  border: "1px solid #e2e2e2",
  borderRadius: 4,
  padding: "0.75rem",
  background: "#fff",
};
const panelHeaderStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
  fontWeight: 600,
  fontSize: "0.9rem",
  color: "#333",
  marginBottom: "0.5rem",
};
const phaseHintStyle: React.CSSProperties = {
  fontWeight: 400,
  color: "#a06b00",
  background: "#fff5d6",
  border: "1px solid #e6d8a8",
  borderRadius: 3,
  fontSize: "0.7rem",
  padding: "0.1rem 0.4rem",
};
const textareaStyle: React.CSSProperties = {
  width: "100%",
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  fontSize: "0.85rem",
  padding: "0.5rem",
  borderRadius: 3,
  border: "1px solid #ddd",
  resize: "vertical",
  boxSizing: "border-box",
};
const nlqInputStyle: React.CSSProperties = {
  width: "100%",
  fontSize: "0.9rem",
  padding: "0.5rem",
  borderRadius: 3,
  border: "1px solid #ddd",
  boxSizing: "border-box",
};
const panelFooterStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "0.75rem",
  marginTop: "0.5rem",
};
const runButtonDisabledStyle: React.CSSProperties = {
  padding: "0.35rem 0.9rem",
  borderRadius: 3,
  border: "1px solid #ccc",
  background: "#f4f4f4",
  color: "#aaa",
  cursor: "not-allowed",
  fontSize: "0.85rem",
};
const runButtonStyle: React.CSSProperties = {
  padding: "0.35rem 0.9rem",
  borderRadius: 3,
  border: "1px solid #0366d6",
  background: "#0366d6",
  color: "white",
  cursor: "pointer",
  fontSize: "0.85rem",
};
const resultsTableStyle: React.CSSProperties = {
  borderCollapse: "collapse",
  width: "100%",
  fontSize: "0.85rem",
};
const resultsHeadStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "0.4rem 0.6rem",
  borderBottom: "2px solid #ddd",
  background: "#fafafa",
  color: "#333",
  fontWeight: 600,
};
const resultsCellStyle: React.CSSProperties = {
  padding: "0.3rem 0.6rem",
  borderBottom: "1px solid #eee",
  color: "#222",
  whiteSpace: "nowrap",
};
const resultsRowAltStyle: React.CSSProperties = {
  background: "#fcfcfc",
};
const errorCardStyle: React.CSSProperties = {
  padding: "0.75rem 1rem",
  background: "#fdecea",
  border: "1px solid #f5c6cb",
  borderRadius: 4,
  fontSize: "0.85rem",
};
const errorCodeStyle: React.CSSProperties = {
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  fontSize: "0.75rem",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "#7a1d1d",
  marginBottom: "0.25rem",
};
const errorMessageStyle: React.CSSProperties = {
  color: "#a23",
  marginBottom: "0.4rem",
};
const errorContextStyle: React.CSSProperties = {
  fontSize: "0.75rem",
  color: "#666",
};
const errorContextPreStyle: React.CSSProperties = {
  margin: "0.4rem 0 0",
  padding: "0.5rem",
  background: "#fff",
  border: "1px solid #eed",
  borderRadius: 3,
  overflowX: "auto",
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  fontSize: "0.75rem",
  color: "#444",
};
const hintStyle: React.CSSProperties = {
  fontSize: "0.75rem",
  color: "#888",
};
const resultsPlaceholderStyle: React.CSSProperties = {
  padding: "1.25rem",
  background: "#fafafa",
  border: "1px dashed #ddd",
  borderRadius: 3,
  color: "#666",
  fontSize: "0.85rem",
};
const emptyStateStyle: React.CSSProperties = {
  marginTop: "1rem",
  padding: "1rem",
  background: "#fafafa",
  border: "1px dashed #ddd",
  borderRadius: 4,
};
const errorBlockStyle: React.CSSProperties = {
  padding: "0.6rem 0.8rem",
  marginTop: "0.75rem",
  background: "#fdecea",
  color: "#a23",
  border: "1px solid #f5c6cb",
  borderRadius: 4,
  fontSize: "0.85rem",
};
